import AppHeader from "@/components/AppHeader";
import InformationDisplay, { formatInformationTime } from "@/components/InformationDisplay";
import NewsRelevanceDisplay from "@/components/NewsRelevanceDisplay";
import {
  useAlertAuth,
  useAlertHistory,
  useAlertOverview,
  useAlertPushConfig,
  useCreateAlertRule,
  useCreateAlertWatchlist,
  useDeleteAlertRule,
  useDeleteAlertWatchlist,
  useRegisterAlertPushSubscription,
  useRevokeAlertPushSubscription,
  useTestAlertPushSubscription,
  useUpdateAlertRule,
  useUpdateAlertWatchlist,
  AlertRequestError,
} from "@/hooks/useAlerts";
import {
  enableBrowserPushDelivery,
  getBrowserPushSupport,
  getExistingBrowserPushSubscription,
  removeBrowserPushSubscription,
  refreshKnownBrowserPushWorker,
  type BrowserPushSubscriptionPayload,
  type BrowserPushSupport,
} from "@/lib/browserPush";
import {
  type AlertCategory,
  type AlertHistoryEvent,
  type AlertRuleInput,
  type AlertRuleOverview,
  type AlertWatchlistOverview,
  type NormalizedSignal,
} from "@/lib/types";
import { ALERT_CATEGORIES } from "@shared/alertRules";
import { Helmet } from "react-helmet-async";
import {
  Bell,
  BellOff,
  BellRing,
  CheckCircle2,
  CircleAlert,
  ExternalLink,
  Loader2,
  LogIn,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

const SEVERITIES = ["low", "medium", "high", "critical"] as const;
const CONFIDENCES = ["low", "medium", "high"] as const;

function readable(value: string): string {
  return value.replace(/-/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function splitTerms(value: string): string[] {
  return value.split(/[\n,]/).map((term) => term.trim()).filter(Boolean);
}

function severityColor(severity: NormalizedSignal["severity"]): string {
  if (severity === "critical") return "#ff4545";
  if (severity === "high") return "#ff9100";
  if (severity === "medium") return "#f4d35e";
  return "#8b9bb4";
}

function RuleInputFromOverview(rule: AlertRuleOverview): AlertRuleInput {
  return {
    watchlistId: rule.watchlistId,
    name: rule.name,
    categories: rule.categories,
    minSeverity: rule.minSeverity,
    minConfidence: rule.minConfidence,
    enabled: rule.enabled,
  };
}

function SignalMatch({ signal }: { signal: NormalizedSignal }) {
  const color = severityColor(signal.severity);
  const content = (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, alignItems: "center", marginBottom: 5 }}>
        <span style={{ color, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em" }}>{signal.severity.toUpperCase()}</span>
        <span style={{ color: "#ff9100", fontSize: 10, textTransform: "uppercase" }}>{readable(signal.category)}</span>
        <span style={{ color: "#777", fontSize: 10 }}>{signal.source}</span>
      </div>
      <strong style={{ color: "#f3f3f3", display: "block", fontSize: 13, lineHeight: 1.35 }}>{signal.title}</strong>
      <InformationDisplay information={signal.information} expandable={false} />
      <p style={{ color: "#888", fontSize: 10, lineHeight: 1.4, margin: "5px 0 0" }}>
        {signal.evidenceQuality
          ? `Evidence quality: ${signal.evidenceQuality.tier}${signal.evidenceQuality.basis ? ` · ${signal.evidenceQuality.basis}` : ""}`
          : `Legacy confidence snapshot: ${signal.confidence}`}
        {" · Analytical confidence: unassessed"}
      </p>
      <NewsRelevanceDisplay relevance={signal.relevance} newsEvidence={signal.newsEvidence} />
      <p style={{ color: "#a7a7a7", fontSize: 12, lineHeight: 1.45, margin: "5px 0 0" }}>{signal.summary}</p>
      {signal.marketImpact && !signal.relevance && !signal.newsEvidence?.length && (
        <p style={{ color: "#7e8da4", fontSize: 11, lineHeight: 1.4, margin: "5px 0 0" }}>
          Impact: {signal.marketImpact}
        </p>
      )}
    </>
  );

  if (!signal.url) return <article style={matchCardStyle}>{content}</article>;
  return (
    <a
      href={signal.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ ...matchCardStyle, display: "block", textDecoration: "none" }}
    >
      {content}
      <span style={{ alignItems: "center", color: "#ff9100", display: "inline-flex", fontSize: 11, gap: 4, marginTop: 8 }}>
        Open source <ExternalLink size={11} />
      </span>
    </a>
  );
}

function historyStatusLabel(status: AlertHistoryEvent["status"]): string {
  switch (status) {
    case "triggered": return "Triggered in Alert Center";
    case "queued": return "Queued for browser delivery";
    case "sending": return "Sending to browser service";
    case "retry": return "Browser delivery retry scheduled";
    case "sent": return "Sent to browser service";
    case "partially_sent": return "Partially sent to browser service";
    case "not_sent": return "Not sent to browser service";
  }
}

function historyStatusColor(status: AlertHistoryEvent["status"]): string {
  if (status === "sent") return "#00c978";
  if (status === "partially_sent" || status === "queued" || status === "sending" || status === "retry") return "#ffb25c";
  if (status === "not_sent") return "#ff9b9b";
  return "#8b9bb4";
}

function AlertHistoryPanel({
  events,
  retentionDays,
  isLoading,
  isFetching,
  isError,
  onRefresh,
}: {
  events: AlertHistoryEvent[];
  retentionDays: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRefresh: () => void;
}) {
  return (
    <section style={{ ...panelStyle, borderColor: "#343434", marginBottom: 18 }} aria-labelledby="alert-history-title">
      <div style={{ alignItems: "flex-start", display: "flex", gap: 12, justifyContent: "space-between" }}>
        <div>
          <p style={{ color: "#ff9100", fontSize: 10, fontWeight: 800, letterSpacing: "0.09em", margin: 0 }}>RECENT ALERT HISTORY</p>
          <h2 id="alert-history-title" style={{ color: "#fff", fontSize: 19, margin: "4px 0 6px" }}>Triggered alerts</h2>
          <p style={{ color: "#8f8f8f", fontSize: 12, lineHeight: 1.45, margin: 0 }}>
            History is private to this account and retained for {retentionDays} days. “Sent” means the browser delivery service accepted it; it does not confirm that a notification was displayed or read.
          </p>
        </div>
        <button type="button" style={{ ...secondaryButtonStyle, minHeight: 30, padding: "5px 8px" }} onClick={onRefresh} disabled={isFetching}>
          <RefreshCw className={isFetching ? "animate-spin" : ""} size={13} /> Refresh
        </button>
      </div>

      {isLoading ? (
        <p style={{ color: "#8d8d8d", margin: "14px 0 0" }}>Loading private alert history…</p>
      ) : isError ? (
        <p role="alert" style={{ color: "#ff9b9b", margin: "14px 0 0" }}>Could not load recent alert history.</p>
      ) : events.length === 0 ? (
        <p style={{ color: "#8d8d8d", margin: "14px 0 0" }}>No matching alert events have been recorded yet. In-app history appears here even when browser delivery is off.</p>
      ) : (
        <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
          {events.map((event) => (
            <article key={event.id} style={{ background: "#050505", border: "1px solid #242424", padding: 12 }}>
              <div style={{ alignItems: "flex-start", display: "flex", gap: 10, justifyContent: "space-between" }}>
                <div>
                  <p style={{ color: historyStatusColor(event.status), fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", margin: 0 }}>{historyStatusLabel(event.status).toUpperCase()}</p>
                  <strong style={{ color: "#f3f3f3", display: "block", fontSize: 14, lineHeight: 1.35, marginTop: 4 }}>{event.title}</strong>
                </div>
                <span style={{ color: "#777", fontSize: 11, textAlign: "right" }}>Triggered: {formatInformationTime(event.triggeredAt)}</span>
              </div>
              <p style={{ color: "#9c9c9c", fontSize: 12, lineHeight: 1.45, margin: "7px 0 0" }}>{event.summary}</p>
              <p style={{ color: "#777", fontSize: 11, lineHeight: 1.45, margin: "8px 0 0" }}>
                {event.ruleName} · {event.watchlistName} · {readable(event.category)} · {readable(event.severity)} severity · {event.source}
              </p>
              <p style={{ color: "#777", fontSize: 11, margin: "5px 0 0" }}>
                {event.evidenceQuality
                  ? `Evidence quality at trigger: ${event.evidenceQuality.tier}${event.evidenceQuality.basis ? ` · ${Array.isArray(event.evidenceQuality.basis) ? event.evidenceQuality.basis.join("; ") : event.evidenceQuality.basis}` : ""}`
                  : `Legacy confidence snapshot at trigger: ${readable(event.confidence)}`}
                {" · Analytical confidence: unassessed"}
              </p>
              <p style={{ color: "#888", fontSize: 11, margin: "7px 0 0" }}>
                {event.deviceCount === 0 ? "In-app event; no browser device was queued." : `${event.sentCount}/${event.deviceCount} browser device${event.deviceCount === 1 ? "" : "s"} accepted.`}
                {event.failedCount > 0 ? ` ${event.failedCount} device${event.failedCount === 1 ? "" : "s"} not sent.` : ""}
              </p>
              <p style={{ color: "#888", fontSize: 11, margin: "7px 0 0" }}>Evidence state at trigger:</p>
              <NewsRelevanceDisplay newsEvidence={event.newsEvidence} />
              <InformationDisplay information={event.information} captured />
              {event.failureCode && <p style={{ color: "#ffb25c", fontSize: 11, margin: "7px 0 0" }}>Last safe delivery reason: {readable(event.failureCode)}.</p>}
              {event.signalUrl && (
                <a href={event.signalUrl} target="_blank" rel="noopener noreferrer" style={{ alignItems: "center", color: "#ff9100", display: "inline-flex", fontSize: 11, gap: 4, marginTop: 9 }}>
                  Open source <ExternalLink size={11} />
                </a>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

const panelStyle: React.CSSProperties = {
  background: "#080808",
  border: "1px solid #2a2a2a",
  padding: 18,
};

const fieldStyle: React.CSSProperties = {
  background: "#050505",
  border: "1px solid #353535",
  color: "#f5f5f5",
  fontSize: 13,
  minHeight: 38,
  padding: "8px 10px",
  width: "100%",
};

const primaryButtonStyle: React.CSSProperties = {
  alignItems: "center",
  background: "#ff9100",
  border: "1px solid #ff9100",
  color: "#111",
  cursor: "pointer",
  display: "inline-flex",
  fontSize: 12,
  fontWeight: 800,
  gap: 7,
  justifyContent: "center",
  minHeight: 38,
  padding: "8px 12px",
};

const secondaryButtonStyle: React.CSSProperties = {
  alignItems: "center",
  background: "transparent",
  border: "1px solid #3c3c3c",
  color: "#c7c7c7",
  cursor: "pointer",
  display: "inline-flex",
  fontSize: 12,
  fontWeight: 700,
  gap: 6,
  justifyContent: "center",
  minHeight: 34,
  padding: "7px 10px",
};

const matchCardStyle: React.CSSProperties = {
  background: "#050505",
  border: "1px solid #242424",
  marginTop: 10,
  padding: 12,
};

const ALERT_PUSH_DEVICE_ID_KEY = "tsm-alert-push-device-id";

function readLocalPushDeviceId(): string | null {
  try {
    return window.localStorage.getItem(ALERT_PUSH_DEVICE_ID_KEY);
  } catch {
    return null;
  }
}

function saveLocalPushDeviceId(id: string) {
  try {
    window.localStorage.setItem(ALERT_PUSH_DEVICE_ID_KEY, id);
  } catch {
    // Browser storage is a convenience only; the server remains authoritative.
  }
}

function clearLocalPushDeviceId() {
  try {
    window.localStorage.removeItem(ALERT_PUSH_DEVICE_ID_KEY);
  } catch {
    // Ignore private-mode storage failures.
  }
}

function safeErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Browser delivery needs attention. Please try again.";
}

function BrowserDeliveryPanel() {
  const pushConfig = useAlertPushConfig(true);
  const register = useRegisterAlertPushSubscription();
  const sendTest = useTestAlertPushSubscription();
  const revoke = useRevokeAlertPushSubscription();
  const [support, setSupport] = useState<BrowserPushSupport>(() => getBrowserPushSupport());
  const [browserSubscription, setBrowserSubscription] = useState<BrowserPushSubscriptionPayload | null>(null);
  const [localDeviceId, setLocalDeviceId] = useState<string | null>(() => readLocalPushDeviceId());
  const [pushMessage, setPushMessage] = useState("");
  const [pushError, setPushError] = useState("");
  const [busy, setBusy] = useState(false);
  const silentWorkerRefreshes = useRef(new Set<string>());

  useEffect(() => {
    const config = pushConfig.data;
    if (!config?.enabled) return;
    const nextSupport = getBrowserPushSupport();
    setSupport(nextSupport);
    if (nextSupport !== "granted") {
      setBrowserSubscription(null);
      return;
    }
    let cancelled = false;
    void getExistingBrowserPushSubscription()
      .then((subscription) => { if (!cancelled) setBrowserSubscription(subscription); })
      .catch(() => { if (!cancelled) setBrowserSubscription(null); });
    return () => { cancelled = true; };
  }, [pushConfig.data?.enabled]);

  useEffect(() => {
    const config = pushConfig.data;
    if (!config?.enabled) return;
    const storedId = readLocalPushDeviceId();
    const active = storedId && config.subscriptions.find((item) => item.id === storedId && item.status === "active");
    if (storedId && !active) {
      clearLocalPushDeviceId();
      setLocalDeviceId(null);
      return;
    }
    setLocalDeviceId(storedId);
  }, [pushConfig.data]);

  const activeDevice = pushConfig.data?.subscriptions.find((item) => item.id === localDeviceId && item.status === "active") ?? null;
  const mutationBusy = busy || register.isPending || sendTest.isPending || revoke.isPending;

  useEffect(() => {
    const deviceId = activeDevice?.id;
    if (!deviceId || !pushConfig.data?.enabled || support !== "granted" || silentWorkerRefreshes.current.has(deviceId)) return;
    // Mark before the asynchronous server check so a rerender cannot start a
    // second registration. A later page load gets a fresh version check.
    silentWorkerRefreshes.current.add(deviceId);
    let cancelled = false;
    void pushConfig.refetch().then(async (latest) => {
      if (cancelled) return;
      const stillActive = latest.data?.enabled
        && latest.data.subscriptions.some((item) => item.id === deviceId && item.status === "active");
      if (!stillActive) {
        clearLocalPushDeviceId();
        setLocalDeviceId((current) => current === deviceId ? null : current);
        return;
      }
      await refreshKnownBrowserPushWorker();
    }).catch(() => {
      // A failed latest-owner check is a hard stop for silent registration.
    });
    return () => { cancelled = true; };
  }, [activeDevice?.id, pushConfig.data?.enabled, pushConfig.refetch, support]);

  const connectBrowser = async () => {
    const config = pushConfig.data;
    if (!config?.enabled || !config.publicKey) return;
    setBusy(true);
    setPushError("");
    setPushMessage("");
    try {
      const subscription = await enableBrowserPushDelivery(config.publicKey);
      let response;
      try {
        response = await register.mutateAsync({ subscription, confirmTransfer: false });
      } catch (error) {
        if (error instanceof AlertRequestError && error.code === "subscription_transfer_confirmation_required") {
          if (!window.confirm("This browser is connected to another alert account. Move browser delivery to this account?")) return;
          response = await register.mutateAsync({ subscription, confirmTransfer: true });
        } else {
          throw error;
        }
      }
      setBrowserSubscription(subscription);
      saveLocalPushDeviceId(response.subscription.id);
      setLocalDeviceId(response.subscription.id);
      setPushMessage(response.transferred
        ? "This browser’s alert delivery was moved to this account."
        : "Browser delivery is active for this device.");
      await pushConfig.refetch();
    } catch (error) {
      setSupport(getBrowserPushSupport());
      setPushError(safeErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const sendBrowserTest = async () => {
    setBusy(true);
    setPushError("");
    setPushMessage("");
    try {
      const subscription = browserSubscription ?? await getExistingBrowserPushSubscription();
      if (!subscription) throw new Error("This browser no longer has an active push subscription. Connect it again.");
      await sendTest.mutateAsync(subscription);
      setBrowserSubscription(subscription);
      setPushMessage("Test notification sent. It should arrive shortly.");
      await pushConfig.refetch();
    } catch (error) {
      setPushError(safeErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const turnOffBrowserDelivery = async () => {
    setBusy(true);
    setPushError("");
    setPushMessage("");
    try {
      const subscription = browserSubscription ?? await getExistingBrowserPushSubscription();
      if (!subscription) throw new Error("This browser no longer has an active push subscription.");
      // Revoke server delivery first, so a local browser failure can never
      // leave outbound delivery active after the user chose Turn off.
      await revoke.mutateAsync(subscription);
      clearLocalPushDeviceId();
      setLocalDeviceId(null);
      setBrowserSubscription(null);
      try {
        await removeBrowserPushSubscription();
        setPushMessage("Browser delivery turned off on this device.");
      } catch {
        setPushMessage("Server delivery is off. Remove the remaining browser permission in your browser settings if needed.");
      }
      await pushConfig.refetch();
    } catch (error) {
      setPushError(safeErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode;
  if (pushConfig.isLoading) {
    body = <p style={{ color: "#8d8d8d", margin: 0 }}>Checking browser delivery configuration…</p>;
  } else if (!pushConfig.data?.enabled) {
    body = <p style={{ color: "#b9b9b9", lineHeight: 1.5, margin: 0 }}><strong style={{ color: "#ffb25c" }}>IN-APP ONLY</strong> — Browser delivery is being prepared. No browser permission has been requested.</p>;
  } else if (support === "unsupported" || support === "insecure") {
    body = <p style={{ color: "#b9b9b9", lineHeight: 1.5, margin: 0 }}>This browser cannot receive secure browser delivery. Your Alert Center remains in-app only.</p>;
  } else if (support === "denied") {
    body = <p style={{ color: "#ffb25c", lineHeight: 1.5, margin: 0 }}>Browser notification permission is blocked. Update your browser settings to enable delivery; we will not prompt again automatically.</p>;
  } else if (activeDevice && support === "granted") {
    body = (
      <>
        <p style={{ color: "#b9b9b9", lineHeight: 1.5, margin: "0 0 10px" }}><strong style={{ color: "#00c978" }}>ACTIVE</strong> — This browser receives future alert-eligible signals.</p>
        {(pushConfig.data.workerHealth === "stale" || pushConfig.data.workerHealth === "degraded") && <p style={{ color: "#ffb25c", fontSize: 12, margin: "0 0 10px" }}>Delivery service needs attention — new rule matches may be delayed.</p>}
        {activeDevice.lastIssueCode && <p style={{ color: "#ffb25c", fontSize: 12, margin: "0 0 10px" }}>Last delivery issue: {readable(activeDevice.lastIssueCode)}.</p>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" style={secondaryButtonStyle} disabled={mutationBusy} onClick={sendBrowserTest}><BellRing size={14} /> Send test</button>
          <button type="button" style={{ ...secondaryButtonStyle, color: "#ff9a9a" }} disabled={mutationBusy} onClick={turnOffBrowserDelivery}><BellOff size={14} /> Turn off</button>
        </div>
      </>
    );
  } else {
    const hasPermission = support === "granted";
    body = (
      <>
        <p style={{ color: "#b9b9b9", lineHeight: 1.5, margin: "0 0 10px" }}>
          {hasPermission ? "Browser permission is ready. Connect this browser to receive only future alert-eligible rule matches." : "No browser permission has been requested. Enable delivery only if you want this browser to receive future alert-eligible rule matches."}
        </p>
        <button type="button" style={primaryButtonStyle} disabled={mutationBusy} onClick={connectBrowser}><BellRing size={15} /> {hasPermission ? "Connect this browser" : "Enable browser delivery"}</button>
      </>
    );
  }

  return (
    <section style={{ ...panelStyle, borderColor: "#343434", marginBottom: 18 }} aria-labelledby="browser-delivery-title">
      <div style={{ alignItems: "flex-start", display: "flex", gap: 12, justifyContent: "space-between" }}>
        <div>
          <p style={{ color: "#ff9100", fontSize: 10, fontWeight: 800, letterSpacing: "0.09em", margin: 0 }}>BROWSER DELIVERY</p>
          <h2 id="browser-delivery-title" style={{ color: "#fff", fontSize: 19, margin: "4px 0 10px" }}>Browser delivery</h2>
        </div>
        <BellRing color="#ff9100" size={23} aria-hidden="true" />
      </div>
      {body}
      {pushMessage && <p role="status" style={{ color: "#80d9ad", fontSize: 12, margin: "12px 0 0" }}>{pushMessage}</p>}
      {pushError && <p role="alert" style={{ color: "#ff9b9b", fontSize: 12, margin: "12px 0 0" }}>{pushError}</p>}
    </section>
  );
}

const Alerts: React.FC = () => {
  const auth = useAlertAuth();
  const signedIn = Boolean(auth.data?.user);
  const overview = useAlertOverview(signedIn);
  const history = useAlertHistory(signedIn);
  const createWatchlist = useCreateAlertWatchlist();
  const updateWatchlist = useUpdateAlertWatchlist();
  const deleteWatchlist = useDeleteAlertWatchlist();
  const createRule = useCreateAlertRule();
  const updateRule = useUpdateAlertRule();
  const deleteRule = useDeleteAlertRule();

  const [watchlistName, setWatchlistName] = useState("");
  const [watchlistTerms, setWatchlistTerms] = useState("");
  const [editingWatchlistId, setEditingWatchlistId] = useState<string | null>(null);
  const [ruleName, setRuleName] = useState("");
  const [ruleWatchlistId, setRuleWatchlistId] = useState("");
  const [ruleCategories, setRuleCategories] = useState<AlertCategory[]>([]);
  const [ruleSeverity, setRuleSeverity] = useState<AlertRuleInput["minSeverity"]>("high");
  const [ruleConfidence, setRuleConfidence] = useState<AlertRuleInput["minConfidence"]>("medium");
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [formError, setFormError] = useState("");

  const watchlists = overview.data?.watchlists ?? [];
  const rules = overview.data?.rules ?? [];

  useEffect(() => {
    if (!ruleWatchlistId && watchlists[0]) setRuleWatchlistId(watchlists[0].id);
    if (ruleWatchlistId && !watchlists.some((watchlist) => watchlist.id === ruleWatchlistId)) {
      setRuleWatchlistId(watchlists[0]?.id ?? "");
    }
  }, [ruleWatchlistId, watchlists]);

  const resetWatchlistForm = () => {
    setEditingWatchlistId(null);
    setWatchlistName("");
    setWatchlistTerms("");
  };

  const resetRuleForm = () => {
    setEditingRuleId(null);
    setRuleName("");
    setRuleWatchlistId(watchlists[0]?.id ?? "");
    setRuleCategories([]);
    setRuleSeverity("high");
    setRuleConfidence("medium");
  };

  const submitWatchlist = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    const input = { name: watchlistName, terms: splitTerms(watchlistTerms) };
    const onSuccess = () => resetWatchlistForm();
    const onError = (error: Error) => setFormError(error.message);
    if (editingWatchlistId) {
      updateWatchlist.mutate({ id: editingWatchlistId, input }, { onSuccess, onError });
    } else {
      createWatchlist.mutate(input, { onSuccess, onError });
    }
  };

  const submitRule = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");
    const input: AlertRuleInput = {
      watchlistId: ruleWatchlistId,
      name: ruleName,
      categories: ruleCategories,
      minSeverity: ruleSeverity,
      minConfidence: ruleConfidence,
      enabled: true,
    };
    const onSuccess = () => resetRuleForm();
    const onError = (error: Error) => setFormError(error.message);
    if (editingRuleId) {
      const existing = rules.find((rule) => rule.id === editingRuleId);
      updateRule.mutate({ id: editingRuleId, input: { ...input, enabled: existing?.enabled ?? true } }, { onSuccess, onError });
    } else {
      createRule.mutate(input, { onSuccess, onError });
    }
  };

  const toggleCategory = (category: AlertCategory) => {
    setRuleCategories((current) => current.includes(category)
      ? current.filter((item) => item !== category)
      : [...current, category]);
  };

  const editWatchlist = (watchlist: AlertWatchlistOverview) => {
    setEditingWatchlistId(watchlist.id);
    setWatchlistName(watchlist.name);
    setWatchlistTerms(watchlist.terms.join(", "));
    setFormError("");
  };

  const editRule = (rule: AlertRuleOverview) => {
    setEditingRuleId(rule.id);
    setRuleName(rule.name);
    setRuleWatchlistId(rule.watchlistId);
    setRuleCategories(rule.categories);
    setRuleSeverity(rule.minSeverity);
    setRuleConfidence(rule.minConfidence);
    setFormError("");
  };

  const removeWatchlist = (watchlist: AlertWatchlistOverview) => {
    if (!window.confirm(`Delete ${watchlist.name} and its dependent alert rules?`)) return;
    deleteWatchlist.mutate(watchlist.id, {
      onSuccess: () => {
        if (editingWatchlistId === watchlist.id) resetWatchlistForm();
        if (ruleWatchlistId === watchlist.id) resetRuleForm();
      },
      onError: (error: Error) => setFormError(error.message),
    });
  };

  const removeRule = (rule: AlertRuleOverview) => {
    if (!window.confirm(`Delete ${rule.name}?`)) return;
    deleteRule.mutate(rule.id, {
      onSuccess: () => {
        if (editingRuleId === rule.id) resetRuleForm();
      },
      onError: (error: Error) => setFormError(error.message),
    });
  };

  const toggleRule = (rule: AlertRuleOverview) => {
    updateRule.mutate({
      id: rule.id,
      input: { ...RuleInputFromOverview(rule), enabled: !rule.enabled },
    }, { onError: (error: Error) => setFormError(error.message) });
  };

  const busy = createWatchlist.isPending || updateWatchlist.isPending || deleteWatchlist.isPending
    || createRule.isPending || updateRule.isPending || deleteRule.isPending;

  return (
    <>
      <Helmet>
        <title>Alerts — Personal Watchlists & Signal Rules | Tylee Smart Markets</title>
        <meta name="description" content="Private, signed-in keyword watchlists and source-backed in-app market signal rules." />
        <link rel="canonical" href="https://tyl.ee/alerts" />
      </Helmet>

      <div className="tsm-home-shell theme-amber" style={{ minHeight: "100vh" }}>
        <a href="#alerts-main" className="skip-link">Skip to main content</a>
        <AppHeader brandText="Alerts" />

        <main id="alerts-main" style={{ margin: "0 auto", maxWidth: 1180, padding: "28px 18px 54px" }}>
          <section style={{ ...panelStyle, borderColor: "rgba(255, 145, 0, 0.68)", marginBottom: 18 }} aria-labelledby="alerts-title">
            <div style={{ alignItems: "flex-start", display: "flex", gap: 16, justifyContent: "space-between" }}>
              <div>
                <p style={{ color: "#ff9100", fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", margin: 0 }}>PERSONAL ALERT CENTER</p>
                <h1 id="alerts-title" style={{ color: "#fff", fontSize: 28, margin: "5px 0 8px" }}>Watch what matters. Keep the rules explicit.</h1>
                <p style={{ color: "#b9b9b9", lineHeight: 1.55, margin: 0, maxWidth: 760 }}>
                  Save private keyword watchlists, set source-backed signal thresholds, and review matching alert-eligible signals in one place.
                  Browser delivery is optional and explicit when enabled for this deployment; email, SMS, and webhooks remain unavailable.
                </p>
              </div>
              <Bell color="#ff9100" size={34} aria-hidden="true" style={{ flexShrink: 0 }} />
            </div>
          </section>

          {auth.isLoading ? (
            <div style={{ color: "#9a9a9a", display: "flex", gap: 9, padding: 30 }}><Loader2 className="animate-spin" size={17} /> Checking account…</div>
          ) : !signedIn ? (
            <section style={{ ...panelStyle, maxWidth: 680 }} aria-labelledby="alerts-sign-in-title">
              <ShieldCheck color="#ff9100" size={24} aria-hidden="true" />
              <h2 id="alerts-sign-in-title" style={{ color: "#fff", fontSize: 20, margin: "12px 0 8px" }}>Sign in to save private alert rules</h2>
              <p style={{ color: "#aaa", lineHeight: 1.5, margin: "0 0 16px" }}>
                Public signals remain available on the Signals page. Saved watchlists are account-owned so another browser or visitor cannot read or change them.
              </p>
              <button
                type="button"
                style={{ ...primaryButtonStyle, opacity: auth.data?.googleEnabled === false ? 0.55 : 1 }}
                disabled={auth.data?.googleEnabled === false}
                onClick={() => { window.location.href = "/api/auth/google?returnTo=/alerts"; }}
              >
                <LogIn size={15} /> Sign in with Google
              </button>
              {auth.data?.googleEnabled === false && (
                <p style={{ color: "#ffb25c", fontSize: 12, marginBottom: 0 }}>Google sign-in is not configured on this deployment yet.</p>
              )}
            </section>
          ) : overview.isLoading ? (
            <div style={{ color: "#9a9a9a", display: "flex", gap: 9, padding: 30 }}><Loader2 className="animate-spin" size={17} /> Loading private alert rules…</div>
          ) : overview.isError ? (
            <section style={panelStyle}>
              <CircleAlert color="#ff4545" size={20} aria-hidden="true" />
              <p style={{ color: "#ff9b9b" }}>Could not load your alert center. {overview.error instanceof Error ? overview.error.message : "Please try again."}</p>
              <button type="button" style={secondaryButtonStyle} onClick={() => overview.refetch()}><RefreshCw size={14} /> Retry</button>
            </section>
          ) : (
            <>
              <section aria-label="Alert center status" style={{ color: "#959595", display: "flex", fontSize: 12, gap: 12, margin: "0 0 18px", flexWrap: "wrap" }}>
                <span><CheckCircle2 color="#00c978" size={14} style={{ marginRight: 5, verticalAlign: "-2px" }} /> Signed in as {auth.data?.user?.email}</span>
                <span>{overview.data?.evaluation?.informationVersion === "tsm-information-v1" ? `${overview.data.liveSignalCount} alert-eligible source signals evaluated` : "Source signal eligibility unknown"}</span>
                <button type="button" style={{ ...secondaryButtonStyle, minHeight: 28, padding: "4px 8px" }} onClick={() => { void overview.refetch(); void history.refetch(); }} disabled={overview.isFetching || history.isFetching}>
                  <RefreshCw className={overview.isFetching || history.isFetching ? "animate-spin" : ""} size={13} /> Refresh matches
                </button>
              </section>

              <BrowserDeliveryPanel />

              <AlertHistoryPanel
                events={history.data?.events ?? []}
                retentionDays={history.data?.retentionDays ?? 30}
                isLoading={history.isLoading}
                isFetching={history.isFetching}
                isError={history.isError}
                onRefresh={() => { void history.refetch(); }}
              />

              <div style={{ display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
                <section style={panelStyle} aria-labelledby="watchlists-title">
                  <div style={{ alignItems: "center", display: "flex", gap: 10, justifyContent: "space-between", marginBottom: 14 }}>
                    <div>
                      <p style={{ color: "#ff9100", fontSize: 10, fontWeight: 800, letterSpacing: "0.09em", margin: 0 }}>KEYWORD SCOPE</p>
                      <h2 id="watchlists-title" style={{ color: "#fff", fontSize: 19, margin: "4px 0 0" }}>Watchlists</h2>
                    </div>
                    <span style={{ color: "#777", fontSize: 12 }}>{watchlists.length}/10</span>
                  </div>

                  <form onSubmit={submitWatchlist} style={{ borderBottom: "1px solid #252525", marginBottom: 16, paddingBottom: 16 }}>
                    <label style={labelStyle} htmlFor="watchlist-name">Watchlist name</label>
                    <input id="watchlist-name" value={watchlistName} onChange={(event) => setWatchlistName(event.target.value)} placeholder="Energy exposure" style={fieldStyle} maxLength={80} required />
                    <label style={{ ...labelStyle, marginTop: 10 }} htmlFor="watchlist-terms">Keywords</label>
                    <textarea id="watchlist-terms" value={watchlistTerms} onChange={(event) => setWatchlistTerms(event.target.value)} placeholder="oil, OPEC, crude" style={{ ...fieldStyle, minHeight: 76, resize: "vertical" }} maxLength={1400} required />
                    <p style={{ color: "#777", fontSize: 11, lineHeight: 1.4, margin: "6px 0 10px" }}>Use commas or new lines. Terms are literal, case-insensitive matches against alert-eligible signal titles, summaries, impacts, and drivers.</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button type="submit" style={primaryButtonStyle} disabled={busy}>
                        {editingWatchlistId ? <Pencil size={14} /> : <Plus size={14} />} {editingWatchlistId ? "Save watchlist" : "Add watchlist"}
                      </button>
                      {editingWatchlistId && <button type="button" style={secondaryButtonStyle} onClick={resetWatchlistForm}>Cancel</button>}
                    </div>
                  </form>

                  {watchlists.length === 0 ? (
                    <p style={{ color: "#888", margin: 0 }}>Create a keyword watchlist before adding a rule.</p>
                  ) : watchlists.map((watchlist) => (
                    <article key={watchlist.id} style={{ borderBottom: "1px solid #222", padding: "12px 0" }}>
                      <div style={{ alignItems: "flex-start", display: "flex", gap: 10, justifyContent: "space-between" }}>
                        <div>
                          <strong style={{ color: "#f1f1f1" }}>{watchlist.name}</strong>
                          <p style={{ color: "#8e8e8e", fontSize: 12, margin: "5px 0 0" }}>{watchlist.terms.join(" · ")}</p>
                        </div>
                        <span style={{ color: watchlist.matchCount ? "#ff9100" : "#777", fontSize: 11 }}>{watchlist.matchCount} match{watchlist.matchCount === 1 ? "" : "es"}</span>
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 9 }}>
                        <button type="button" style={secondaryButtonStyle} onClick={() => editWatchlist(watchlist)}><Pencil size={13} /> Edit</button>
                        <button type="button" style={{ ...secondaryButtonStyle, color: "#ff9a9a" }} onClick={() => removeWatchlist(watchlist)} disabled={busy}><Trash2 size={13} /> Delete</button>
                      </div>
                      {watchlist.matches.map((signal) => <SignalMatch key={`${watchlist.id}-${signal.id}`} signal={signal} />)}
                      {watchlist.matchCount > watchlist.matches.length && <p style={moreMatchesStyle}>Showing the first {watchlist.matches.length} of {watchlist.matchCount} matches.</p>}
                    </article>
                  ))}
                </section>

                <section style={panelStyle} aria-labelledby="rules-title">
                  <div style={{ alignItems: "center", display: "flex", gap: 10, justifyContent: "space-between", marginBottom: 14 }}>
                    <div>
                      <p style={{ color: "#ff9100", fontSize: 10, fontWeight: 800, letterSpacing: "0.09em", margin: 0 }}>SOURCE-BACKED THRESHOLDS</p>
                      <h2 id="rules-title" style={{ color: "#fff", fontSize: 19, margin: "4px 0 0" }}>Alert rules</h2>
                    </div>
                    <span style={{ color: "#777", fontSize: 12 }}>{rules.length}/30</span>
                  </div>

                  <form onSubmit={submitRule} style={{ borderBottom: "1px solid #252525", marginBottom: 16, opacity: watchlists.length ? 1 : 0.55, paddingBottom: 16 }}>
                    <fieldset disabled={!watchlists.length || busy} style={{ border: 0, margin: 0, padding: 0 }}>
                      <label style={labelStyle} htmlFor="rule-name">Rule name</label>
                      <input id="rule-name" value={ruleName} onChange={(event) => setRuleName(event.target.value)} placeholder="High-evidence-quality energy" style={fieldStyle} maxLength={80} required />
                      <label style={{ ...labelStyle, marginTop: 10 }} htmlFor="rule-watchlist">Watchlist</label>
                      <select id="rule-watchlist" value={ruleWatchlistId} onChange={(event) => setRuleWatchlistId(event.target.value)} style={fieldStyle} required>
                        {watchlists.map((watchlist) => <option key={watchlist.id} value={watchlist.id}>{watchlist.name}</option>)}
                      </select>
                      <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr", marginTop: 10 }}>
                        <label style={labelStyle}>Minimum severity
                          <select value={ruleSeverity} onChange={(event) => setRuleSeverity(event.target.value as AlertRuleInput["minSeverity"])} style={{ ...fieldStyle, marginTop: 5 }}>
                            {SEVERITIES.map((severity) => <option key={severity} value={severity}>{readable(severity)}</option>)}
                          </select>
                        </label>
                        <label style={labelStyle}>Minimum evidence quality
                          <select value={ruleConfidence} onChange={(event) => setRuleConfidence(event.target.value as AlertRuleInput["minConfidence"])} style={{ ...fieldStyle, marginTop: 5 }}>
                            {CONFIDENCES.map((confidence) => <option key={confidence} value={confidence}>{readable(confidence)}</option>)}
                          </select>
                        </label>
                      </div>
                      <fieldset style={{ border: "1px solid #303030", margin: "12px 0", padding: 10 }}>
                        <legend style={{ color: "#adadad", fontSize: 11, padding: "0 5px" }}>Signal categories (leave clear for any)</legend>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {ALERT_CATEGORIES.map((category) => (
                            <label key={category} style={{ alignItems: "center", color: "#bdbdbd", display: "inline-flex", fontSize: 11, gap: 5 }}>
                              <input type="checkbox" checked={ruleCategories.includes(category)} onChange={() => toggleCategory(category)} /> {readable(category)}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="submit" style={primaryButtonStyle}>
                          {editingRuleId ? <Pencil size={14} /> : <Plus size={14} />} {editingRuleId ? "Save rule" : "Add rule"}
                        </button>
                        {editingRuleId && <button type="button" style={secondaryButtonStyle} onClick={resetRuleForm}>Cancel</button>}
                      </div>
                    </fieldset>
                  </form>

                  {rules.length === 0 ? (
                    <p style={{ color: "#888", margin: 0 }}>Rules make the match criteria deliberate. They require validated current observations, a healthy provider, and valid delivery. Periodic, delayed, and failed-refresh records are excluded.</p>
                  ) : rules.map((rule) => (
                    <article key={rule.id} style={{ borderBottom: "1px solid #222", padding: "12px 0" }}>
                      <div style={{ alignItems: "flex-start", display: "flex", gap: 10, justifyContent: "space-between" }}>
                        <div>
                          <strong style={{ color: "#f1f1f1" }}>{rule.name}</strong>
                          <p style={{ color: "#8e8e8e", fontSize: 12, lineHeight: 1.45, margin: "5px 0 0" }}>
                            {rule.watchlistName ?? "Removed watchlist"} · {readable(rule.minSeverity)}+ severity · {readable(rule.minConfidence)}+ evidence quality · {rule.categories.length ? rule.categories.map(readable).join(", ") : "any category"}
                          </p>
                        </div>
                        <span style={{ color: rule.enabled ? "#00c978" : "#777", fontSize: 10, fontWeight: 800, letterSpacing: "0.08em" }}>{rule.enabled ? "ACTIVE" : "PAUSED"}</span>
                      </div>
                      <div style={{ display: "flex", gap: 8, marginTop: 9, flexWrap: "wrap" }}>
                        <button type="button" style={secondaryButtonStyle} onClick={() => toggleRule(rule)} disabled={busy}>{rule.enabled ? "Pause" : "Resume"}</button>
                        <button type="button" style={secondaryButtonStyle} onClick={() => editRule(rule)}><Pencil size={13} /> Edit</button>
                        <button type="button" style={{ ...secondaryButtonStyle, color: "#ff9a9a" }} onClick={() => removeRule(rule)} disabled={busy}><Trash2 size={13} /> Delete</button>
                        <span style={{ alignSelf: "center", color: rule.matchCount ? "#ff9100" : "#777", fontSize: 11 }}>{rule.matchCount} match{rule.matchCount === 1 ? "" : "es"}</span>
                      </div>
                      {rule.matches.map((signal) => <SignalMatch key={`${rule.id}-${signal.id}`} signal={signal} />)}
                      {rule.matchCount > rule.matches.length && <p style={moreMatchesStyle}>Showing the first {rule.matches.length} of {rule.matchCount} matches.</p>}
                    </article>
                  ))}
                </section>
              </div>
              {formError && <p role="alert" style={{ color: "#ff9b9b", marginTop: 16 }}>{formError}</p>}
            </>
          )}
        </main>
      </div>
    </>
  );
};

const labelStyle: React.CSSProperties = {
  color: "#b7b7b7",
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.03em",
};

const moreMatchesStyle: React.CSSProperties = {
  color: "#737373",
  fontSize: 11,
  margin: "9px 0 0",
};

export default Alerts;
